import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Trophy, Medal, Award } from "lucide-react";

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
];

const fmtMoney = (n) =>
  `£${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const RANK_CFG = {
  1: { row: "bg-yellow-50", badge: "bg-yellow-400 text-yellow-900", icon: Trophy, iconColor: "text-yellow-500" },
  2: { row: "bg-slate-50", badge: "bg-slate-300 text-slate-800", icon: Medal, iconColor: "text-slate-400" },
  3: { row: "bg-orange-50", badge: "bg-orange-400 text-orange-900", icon: Award, iconColor: "text-orange-500" },
};

export default function Leaderboards() {
  const [period, setPeriod] = useState("week");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/leaderboards?period=${period}`)
      .then((res) => setRows(res.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <Card className="border-slate-200" data-testid="leaderboards-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Top Fundraisers
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">Ranked by net signups, then revenue estimate.</p>
          </div>
          <div className="flex gap-1" data-testid="leaderboards-period-buttons">
            {PERIODS.map((p) => (
              <Button
                key={p.value}
                size="sm"
                variant={period === p.value ? "default" : "outline"}
                onClick={() => setPeriod(p.value)}
                data-testid={`leaderboard-period-${p.value}`}
                className={period === p.value ? "bg-yellow-500 hover:bg-yellow-600 text-white" : ""}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-center py-6 text-slate-400">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-center py-6 text-slate-400" data-testid="leaderboards-empty">
            No fundraiser activity in this period.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto" data-testid="leaderboards-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="py-2 pr-2 font-medium text-slate-600 w-12">#</th>
                  <th className="py-2 pr-4 font-medium text-slate-600">Fundraiser</th>
                  <th className="py-2 pr-4 font-medium text-slate-600 text-right">Signups</th>
                  <th className="py-2 pr-4 font-medium text-slate-600 text-right">Cancelled</th>
                  <th className="py-2 pr-4 font-medium text-slate-600 text-right">Net</th>
                  <th className="py-2 pr-4 font-medium text-slate-600 text-right">Revenue Est.</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const rank = idx + 1;
                  const cfg = RANK_CFG[rank];
                  const Icon = cfg?.icon;
                  return (
                    <tr
                      key={r.fundraiserId}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${cfg?.row || ""}`}
                      data-testid={`leaderboard-row-${rank}`}
                    >
                      <td className="py-2 pr-2">
                        <span
                          className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                            cfg?.badge || "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {rank}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-2">
                          {Icon && <Icon className={`h-4 w-4 ${cfg.iconColor}`} />}
                          <span className="font-medium text-slate-900">{r.fundraiserName}</span>
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">{r.signups}</td>
                      <td className="py-2 pr-4 text-right font-mono text-amber-700">{r.cancelled}</td>
                      <td className="py-2 pr-4 text-right font-mono font-semibold text-slate-900">
                        {r.netSignups}
                      </td>
                      <td className="py-2 pr-4 text-right font-mono text-emerald-700">
                        {fmtMoney(r.revenue)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
